import _ from 'lodash'
import moment from 'moment'
import { createSelector } from 'reselect'

import { DEFAULT_DATE_FORMAT } from 'constants/date'
import { Option } from 'constants/models'
import { AllergyTypes, CarerChildRelations, Child } from 'services/child/constants'
import { DIETARY_NO_PREFERENCE } from 'services/legacy/child/constants'
import { NurserySettingsFormBuilder } from 'services/nurseries/models'

import { isSameDay } from 'utils/date'

import { ADDITIONAL_VALUE_PREFIX, getBooleanValuesFromString } from 'services/legacy/child/selectors'

import { SelectedImage } from 'components/PhotoUpload/PhotoUpload'

import { RootState } from 'core/reducers'
import i18n from 'translations'

import {
  FormPreviewCarerChildRelation,
  FormPreviewFormValues,
  RegistrationForm,
  RegistrationFormBody,
  RegistrationFormBodyCarerChildRelation,
  RegistrationFormObjectAction,
  RegistrationFormStatus,
} from '../models'

const getRegistrationFormsState = (state: RootState) => state?.registrationForms

export const getRegistrationForms = createSelector([getRegistrationFormsState], (state) => state?.single?.data)

interface GetSendRegistrationFormBodyParams {
  childId: number
  recipientId: number
}

export const getSendRegistrationFormBody = ({ childId, recipientId }: GetSendRegistrationFormBodyParams) => ({
  child: { id: childId },
  recipient: { id: recipientId },
})

export const getInitialValues = async ({
  child,
  nurseryFormBuilderSettings,
  // onGetImage,
  registrationForm,
}: {
  child: Child
  nurseryFormBuilderSettings: NurserySettingsFormBuilder
  onGetImage: (url: string) => Promise<SelectedImage>
  registrationForm: RegistrationForm
}): Promise<FormPreviewFormValues> => {
  const initialValues: FormPreviewFormValues = {
    data: {
      information: {},
      profile: {},
    },
  }

  if (!registrationForm || !nurseryFormBuilderSettings) {
    return initialValues
  }

  // if (registrationForm.signature?.url) {
  //   const signature = await onGetImage(registrationForm.signature.url)

  //   initialValues.signature = signature
  // }

  const assignIfAllowed = (
    path: string,
    value: any,
  ) => {
    const fieldsAllowed = _.get(nurseryFormBuilderSettings.fields, path)

    if (fieldsAllowed && value !== undefined) {
      _.set(initialValues.data, path.replace('child.', ''), value)
    }
  }

  const { data } = registrationForm

  const fieldsToAssign = [
    {
      path: 'child.firstName',
      value: !_.isUndefined(data?.firstName) ? data.firstName : child?.firstName,
    },
    {
      path: 'child.middleNames',
      value: !_.isUndefined(data?.middleNames) ? data.middleNames : child?.middleNames,
    },
    {
      path: 'child.surname',
      value: !_.isUndefined(data?.surname) ? data.surname : child?.surname,
    },
    {
      path: 'child.gender',
      value: !_.isUndefined(data?.gender) ? data.gender : child?.gender,
    },
    {
      path: 'child.birthDate',
      value: !_.isUndefined(data?.birthDate) ? data.birthDate : child?.birthDate,
    },
    {
      path: 'child.photo',
      value: !_.isUndefined(data?.photo) ? data.photo : child?.photo,
    },
    {
      path: 'child.displayName',
      value: !_.isUndefined(data?.displayName) ? data.displayName : child?.displayName,
    },
    {
      path: 'child.religion',
      value: !_.isUndefined(data?.religion) ? data.religion : child?.religion,
    },
    {
      path: 'child.codeReference',
      value: !_.isUndefined(data?.codeReference) ? data.codeReference : child?.codeReference,
    },
    {
      path: 'child.taxFreeChildcareCodeReference',
      value: !_.isUndefined(data?.taxFreeChildcareCodeReference)
        ? data.taxFreeChildcareCodeReference
        : child?.taxFreeChildcareCodeReference,
    },
    {
      path: 'child.fundingCode',
      value: !_.isUndefined(data?.fundingCode) ? data.fundingCode : child?.fundingCode,
    },
    {
      path: 'child.information.address',
      value: !_.isUndefined(data?.information?.address) ? data.information.address : child?.information?.address,
    },
    {
      path: 'child.information.postcode',
      value: !_.isUndefined(data?.information?.postcode) ? data.information.postcode : child?.information?.postcode,
    },
    {
      path: 'child.information.allergies',
      value: !_.isUndefined(data?.information?.allergies)
        ? data.information.allergies
        : child?.information?.allergies,
    },
    {
      path: 'child.information.allergiesDetails',
      value: !_.isUndefined(data?.information?.allergiesDetails)
        ? data.information.allergiesDetails
        : child?.information?.allergiesDetails,
    },
    {
      path: 'child.information.dietaryReq',
      value: !_.isUndefined(data?.information?.dietaryReq)
        ? data.information.dietaryReq
        : child?.information?.dietaryReq,
    },
    {
      path: 'child.information.dietaryReqDesc',
      value: !_.isUndefined(data?.information?.dietaryReqDesc)
        ? data.information.dietaryReqDesc
        : child?.information?.dietaryReqDesc,
    },
    {
      path: 'child.information.canReceiveMedicine',
      value: !_.isUndefined(data?.information?.canReceiveMedicine)
        ? data.information.canReceiveMedicine
        : child?.information?.canReceiveMedicine,
    },
    {
      path: 'child.information.toiletTrained',
      value: !_.isUndefined(data?.information?.toiletTrained)
        ? data.information.toiletTrained
        : child?.information?.toiletTrained,
    },
    {
      path: 'child.information.canBePhotographed',
      value: !_.isUndefined(data?.information?.canBePhotographed)
        ? data.information.canBePhotographed
        : child?.information?.canBePhotographed,
    },
    {
      path: 'child.information.canBeTaggedInGroupPhotos',
      value: !_.isUndefined(data?.information?.canBeTaggedInGroupPhotos)
        ? data.information.canBeTaggedInGroupPhotos
        : child?.information?.canBeTaggedInGroupPhotos,
    },
    {
      path: 'child.information.socialMediaPhotoSharingPermitted',
      value: !_.isUndefined(data?.information?.socialMediaPhotoSharingPermitted)
        ? data.information.socialMediaPhotoSharingPermitted
        : child?.information?.socialMediaPhotoSharingPermitted,
    },
    {
      path: 'child.information.closedSocialMediaPhotoSharingPermitted',
      value: !_.isUndefined(data?.information?.closedSocialMediaPhotoSharingPermitted)
        ? data.information.closedSocialMediaPhotoSharingPermitted
        : child?.information?.closedSocialMediaPhotoSharingPermitted,
    },
    {
      path: 'child.information.useOnSiteMediaPermitted',
      value: !_.isUndefined(data?.information?.useOnSiteMediaPermitted)
        ? data.information.useOnSiteMediaPermitted
        : child?.information?.useOnSiteMediaPermitted,
    },
    {
      path: 'child.information.otherMarketingMaterialPhotoSharingPermitted',
      value: !_.isUndefined(data?.information?.otherMarketingMaterialPhotoSharingPermitted)
        ? data.information.otherMarketingMaterialPhotoSharingPermitted
        : child?.information?.otherMarketingMaterialPhotoSharingPermitted,
    },
    {
      path: 'child.information.drinkingMethod',
      value: !_.isUndefined(data?.information?.drinkingMethod)
        ? data.information.drinkingMethod
        : child?.information?.drinkingMethod,
    },
    {
      path: 'child.information.canConsumeFoodWithNutTraces',
      value: !_.isUndefined(data?.information?.canConsumeFoodWithNutTraces)
        ? data.information.canConsumeFoodWithNutTraces
        : child?.information?.canConsumeFoodWithNutTraces,
    },
    {
      path: 'child.information.hasPermissionToBeTakenToHospital',
      value: !_.isUndefined(data?.information?.hasPermissionToBeTakenToHospital)
        ? data.information.hasPermissionToBeTakenToHospital
        : child?.information?.hasPermissionToBeTakenToHospital,
    },
    {
      path: 'child.profile.siblings',
      value: !_.isUndefined(data?.profile?.siblings) ? data?.profile?.siblings : child?.profile?.siblings,
    },
    {
      path: 'child.profile.familyPosition',
      value: !_.isUndefined(data?.profile?.familyPosition)
        ? data.profile.familyPosition
        : child?.profile?.familyPosition,
    },
    {
      path: 'child.profile.familyMaritalStatus',
      value: !_.isUndefined(data?.profile?.familyMaritalStatus)
        ? data.profile.familyMaritalStatus
        : child?.profile?.familyMaritalStatus,
    },
    {
      path: 'child.profile.parentsAreServingArmedForcesPersonnel',
      value: !_.isUndefined(data?.profile?.parentsAreServingArmedForcesPersonnel)
        ? data.profile.parentsAreServingArmedForcesPersonnel
        : child?.profile?.parentsAreServingArmedForcesPersonnel,
    },
    {
      path: 'child.profile.medicalInformation',
      value: !_.isUndefined(data?.profile?.medicalInformation)
        ? data.profile.medicalInformation
        : child?.profile?.medicalInformation,
    },
    {
      path: 'child.profile.isFussyEater',
      value: !_.isUndefined(data?.profile?.isFussyEater)
        ? data.profile.isFussyEater
        : child?.profile?.isFussyEater,
    },
    {
      path: 'child.profile.languageCapability',
      value: !_.isUndefined(data?.profile?.languageCapability)
        ? data.profile.languageCapability
        : child?.profile?.languageCapability,
    },
    {
      path: 'child.profile.movementCapability',
      value: !_.isUndefined(data?.profile?.movementCapability)
        ? data.profile.movementCapability
        : child?.profile?.movementCapability,
    },
    {
      path: 'child.profile.childBehavior',
      value: !_.isUndefined(data?.profile?.childBehavior)
        ? data.profile.childBehavior
        : child?.profile?.childBehavior,
    },
    {
      path: 'child.profile.routine',
      value: !_.isUndefined(data?.profile?.routine)
        ? data.profile.routine
        : child?.profile?.routine,
    },
    {
      path: 'child.profile.attendsOtherSetting',
      value: !_.isUndefined(data?.profile?.attendsOtherSetting)
        ? data.profile.attendsOtherSetting
        : child?.profile?.attendsOtherSetting,
    },
    {
      path: 'child.profile.toysPreference',
      value: !_.isUndefined(data?.profile?.toysPreference)
        ? data.profile.toysPreference
        : child?.profile?.toysPreference,
    },
    {
      path: 'child.profile.booksPreference',
      value: !_.isUndefined(data?.profile?.booksPreference)
        ? data.profile.booksPreference
        : child?.profile?.booksPreference,
    },
    {
      path: 'child.profile.foodsPreference',
      value: !_.isUndefined(data?.profile?.foodsPreference)
        ? data.profile.foodsPreference
        : child?.profile?.foodsPreference,
    },
    {
      path: 'child.profile.otherPreference',
      value: !_.isUndefined(data?.profile?.otherPreference)
        ? data.profile.otherPreference
        : child?.profile?.otherPreference,
    },
  ]

  _.each(fieldsToAssign, ({ path, value }) => assignIfAllowed(path, value))

  if (
    (registrationForm.data?.ethnicity?.id || child?.ethnicity?.id)
    && nurseryFormBuilderSettings.fields.child?.ethnicity
  ) {
    if (registrationForm.data?.ethnicity?.id) {
      initialValues.data.ethnicity = {
        value: registrationForm.data.ethnicity.id,
      } as Option
    } else {
      initialValues.data.ethnicity = {
        label: child.ethnicity.name,
        value: child.ethnicity.id,
      } as Option
    }
  }

  if (
    (registrationForm.data?.profile?.familySpokenLanguage || child?.profile?.familySpokenLanguage)
    && nurseryFormBuilderSettings.fields.child?.profile?.familySpokenLanguage
  ) {
    const familySpokenLanguage = (
      registrationForm.data?.profile?.familySpokenLanguage?.split(',')
      || child?.profile?.familySpokenLanguage?.split(',')
    )

    initialValues.data.profile.familySpokenLanguage = _.map(familySpokenLanguage, (language) => ({
      value: language,
    }))
  }

  const applyPermissions = (
    registrationForm.data?.information?.applyPermissions
    || child?.information?.applyPermissions
    || []
  )

  if (applyPermissions.length) {
    initialValues.data.information.applyPermissions = {}

    if (
      _.includes(applyPermissions, 'sunCream')
      && nurseryFormBuilderSettings.fields.child?.information.applyPermissions?.sunCream
    ) {
      initialValues.data.information.applyPermissions.sunCream = true
    }

    if (
      _.includes(applyPermissions, 'patches')
      && nurseryFormBuilderSettings.fields.child?.information.applyPermissions?.patches
    ) {
      initialValues.data.information.applyPermissions.patches = true
    }

    if (
      _.includes(applyPermissions, 'nappyCream')
      && nurseryFormBuilderSettings.fields.child?.information.applyPermissions?.nappyCream
    ) {
      initialValues.data.information.applyPermissions.nappyCream = true
    }

    if (
      _.includes(applyPermissions, 'facePaint')
      && nurseryFormBuilderSettings.fields.child?.information.applyPermissions?.facePaint
    ) {
      initialValues.data.information.applyPermissions.facePaint = true
    }
  }

  const travelPermissions = (
    registrationForm.data?.information?.travelPermissions
    || child?.information?.travelPermissions
    || []
  )

  if (travelPermissions.length) {
    initialValues.data.information.travelPermissions = {}

    if (
      _.includes(travelPermissions, 'canGoToExternalOutgoings')
      && nurseryFormBuilderSettings.fields.child?.information.travelPermissions?.canGoToExternalOutgoings
    ) {
      initialValues.data.information.travelPermissions.canGoToExternalOutgoings = true
    }

    if (
      _.includes(travelPermissions, 'canTakeTheBus')
      && nurseryFormBuilderSettings.fields.child?.information.travelPermissions?.canTakeTheBus
    ) {
      initialValues.data.information.travelPermissions.canTakeTheBus = true
    }
  }

  const daytimeSleepingBehavior = (
    !_.isUndefined(data?.information?.daytimeSleepingBehavior)
      ? data.information.daytimeSleepingBehavior
      : child?.information?.daytimeSleepingBehavior
  )

  if (daytimeSleepingBehavior
    && nurseryFormBuilderSettings.fields.child?.information.daytimeSleepingBehavior
  ) {
    const { details, values } = getBooleanValuesFromString(daytimeSleepingBehavior)

    initialValues.data.information.daytimeSleepingBehavior = (
      ADDITIONAL_VALUE_PREFIX === values?.trim()
    )
    initialValues.data.information.daytimeSleepingBehaviorDetails = details?.trim()
  }

  const specialEducationalNeeds = (
    !_.isUndefined(data?.profile?.specialEducationalNeeds)
      ? data.profile.specialEducationalNeeds
      : child?.profile?.specialEducationalNeeds
  )

  if (specialEducationalNeeds
    && nurseryFormBuilderSettings.fields.child?.profile.specialEducationalNeeds
  ) {
    const { details, values } = getBooleanValuesFromString(specialEducationalNeeds)

    initialValues.data.profile.specialEducationalNeeds = (
      ADDITIONAL_VALUE_PREFIX === values?.trim()
    )
    initialValues.data.profile.specialEducationalNeedsDetails = details?.trim()
  }

  const englishAsAdditionalLanguage = (
    !_.isUndefined(data?.profile?.englishAsAdditionalLanguage)
      ? data.profile.englishAsAdditionalLanguage
      : child?.profile?.englishAsAdditionalLanguage
  )

  if (englishAsAdditionalLanguage
    && nurseryFormBuilderSettings.fields.child?.profile.englishAsAdditionalLanguage
  ) {
    const { details, values } = getBooleanValuesFromString(englishAsAdditionalLanguage)

    initialValues.data.profile.englishAsAdditionalLanguage = (
      ADDITIONAL_VALUE_PREFIX === values?.trim()
    )
    initialValues.data.profile.englishAsAdditionalLanguageDetails = details?.trim()
  }

  let finalCarerChildRelations = []
  const carerFields = [
    'firstName',
    'surname',
    'telephone',
    'workTelephone',
    'address',
    'email',
    'mobile',
    'occupation',
    'postCode',
    'photo',
  ]

  _.each(data?.carerChildRelations, (carerChildRelation: FormPreviewCarerChildRelation) => {
    const finalItem: FormPreviewCarerChildRelation & { carer: { objectId?: number } } = {
      carer: {},
    }

    if (carerChildRelation?.objectId) {
      finalItem.objectId = carerChildRelation.objectId

      if (RegistrationFormObjectAction.DELETE === carerChildRelation?.objectAction) {
        finalItem.objectAction = RegistrationFormObjectAction.DELETE

        finalCarerChildRelations.push(finalItem)

        return
      }
    }

    finalItem.carer = {}

    if (carerChildRelation.carer?.objectId) {
      finalItem.carer.objectId = carerChildRelation.carer.objectId
    }

    if (nurseryFormBuilderSettings.fields.child?.carerChildRelations?.relation) {
      finalItem.relation = carerChildRelation.relation?.value ? {
        label: carerChildRelation.relation.value,
        value: carerChildRelation.relation.value,
      } : carerChildRelation.relation
    }

    if (nurseryFormBuilderSettings.fields.child?.carerChildRelations?.carer?.title) {
      if (carerChildRelation?.carer?.title) {
        finalItem.carer.title = {
          value: carerChildRelation.carer.title,
        } as unknown as Option<string>
      } else if (null === carerChildRelation?.carer?.title) {
        finalItem.carer.title = null
      }
    }

    _.forEach(carerFields, (field: string) => {
      const isFieldEnabled = nurseryFormBuilderSettings.fields.child?.carerChildRelations?.carer?.[field]

      if (!isFieldEnabled) {
        return
      }

      if (!_.isUndefined(carerChildRelation?.carer?.[field])) {
        finalItem.carer[field] = carerChildRelation.carer[field]
      }
    })

    if (!_.keys(finalItem.carer).length) {
      delete finalItem.carer
    }

    if (1 === _.keys(finalItem).length && finalItem.objectId) {
      return
    }

    finalCarerChildRelations.push(finalItem)
  })

  _.each(child?.carerChildRelations, (carerChildRelation: CarerChildRelations) => {
    const relatedCarerChildRelation = _.find(finalCarerChildRelations, { objectId: carerChildRelation.id })

    const finalItem: FormPreviewCarerChildRelation & { carer: { objectId?: number } } = {
      carer: {
        objectId: carerChildRelation.carer.id as number,
      },
      objectId: carerChildRelation.id,
    }

    if (relatedCarerChildRelation?.objectAction === RegistrationFormObjectAction.DELETE) {
      finalItem.objectAction = RegistrationFormObjectAction.DELETE
    }

    if (nurseryFormBuilderSettings.fields.child?.carerChildRelations?.relation) {
      if (relatedCarerChildRelation?.relation) {
        finalItem.relation = {
          label: relatedCarerChildRelation.relation,
          value: relatedCarerChildRelation.relation,
        }
      } else if (null === relatedCarerChildRelation?.relation) {
        finalItem.relation = null
      } else if (carerChildRelation?.relation) {
        finalItem.relation = {
          label: carerChildRelation.relation,
          value: carerChildRelation.relation,
        }
      }
    }

    if (nurseryFormBuilderSettings.fields.child?.carerChildRelations?.carer?.title) {
      if (!_.isUndefined(relatedCarerChildRelation?.carer?.title)) {
        finalItem.carer.title = relatedCarerChildRelation.carer.title
      } else if (carerChildRelation?.carer?.title) {
        finalItem.carer.title = {
          label: carerChildRelation.carer.title,
          value: carerChildRelation.carer.title,
        }
      }
    }

    _.forEach(carerFields, (field: string) => {
      const isFieldEnabled = nurseryFormBuilderSettings.fields.child?.carerChildRelations?.carer?.[field]

      if (!isFieldEnabled) {
        return
      }

      if (!_.isUndefined(relatedCarerChildRelation?.carer?.[field])) {
        finalItem.carer[field] = relatedCarerChildRelation.carer[field]
      } else {
        finalItem.carer[field] = carerChildRelation?.carer?.[field]
      }
    })

    if (!_.keys(finalItem.carer).length) {
      delete finalItem.carer
    }

    if (1 === _.keys(finalItem).length && finalItem.objectId) {
      return
    }

    if (relatedCarerChildRelation) {
      finalCarerChildRelations = _.map(finalCarerChildRelations, (item) => {
        if (item.objectId === finalItem.objectId) {
          return finalItem
        }

        return item
      })
    } else {
      finalCarerChildRelations.push(finalItem)
    }
  })

  if (finalCarerChildRelations.length) {
    initialValues.data.carerChildRelations = finalCarerChildRelations
  }

  return initialValues
}

const getDiff = (current, previous) => {
  if (!_.isObject(current) || _.isArray(current) || null === current) {
    return !_.isEqual(current, previous) ? current : undefined
  }

  if (
    _.isObject(current)
    && (current.value || 0 === current.value)
    && 2 === _.keys(current).length && _.includes(_.keys(current), 'label')
  ) {
    return current.value !== previous ? current : undefined
  }

  const diff = {}
  _.forEach(_.keys(current), (key) => {
    const subDiff = getDiff(current[key], previous?.[key])

    if (subDiff !== undefined) {
      diff[key] = subDiff
    }
  })

  return _.isEmpty(diff) ? undefined : diff
}

const getOnlyChangedValues = (formValues, originalValues) => {
  const reducedFormValues = _.omit(formValues,
    'photo',
    'ethnicity',
    'birthDate',
    'carerChildRelations',
    'information.applyPermissions',
    'information.travelPermissions',
    'information.daytimeSleepingBehavior',
    'information.daytimeSleepingBehaviorDetails',
    'profile.familySpokenLanguage',
    'profile.englishAsAdditionalLanguage',
    'profile.englishAsAdditionalLanguageDetails',
    'profile.specialEducationalNeeds',
    'profile.specialEducationalNeedsDetails',
  )

  const diff = getDiff(reducedFormValues, originalValues) || {}

  diff.carerChildRelations = []

  _.each(
    formValues.carerChildRelations,
    (carerChildRelation: FormPreviewCarerChildRelation) => {
      if (carerChildRelation.objectId) {
        const originalCarer = _.find(originalValues.carerChildRelations, { id: carerChildRelation.objectId })

        if (originalCarer) {
          const reducedCarerChildRelation = _.omit(carerChildRelation,
            'carer.title',
            'carer.photo',
            'relation',
          )

          const diffs = getDiff(reducedCarerChildRelation, originalCarer)

          if (carerChildRelation.carer?.photo !== originalCarer.carer?.photo) {
            if (!diffs.carer) {
              diffs.carer = {}
            }

            diffs.carer.photo = carerChildRelation.carer.photo
          }

          if (carerChildRelation.carer?.title?.value !== originalCarer.carer?.title) {
            if (!diffs.carer) {
              diffs.carer = {}
            }

            diffs.carer.title = carerChildRelation.carer.title
          }

          if (null === carerChildRelation.relation && undefined === originalCarer.relation) {
            diffs.relation = undefined
          } else if (null === carerChildRelation.relation) {
            diffs.relation = null
          } else if (carerChildRelation.relation?.label !== originalCarer.relation) {
            diffs.relation = carerChildRelation.relation
          }

          diff.carerChildRelations.push(diffs)
        }

        return
      }

      diff.carerChildRelations.push(carerChildRelation)
    },
  )

  if (!diff.carerChildRelations.length) {
    delete diff.carerChildRelations
  }

  if (formValues.photo !== originalValues.photo) {
    _.set(diff, 'photo', formValues.photo)
  }

  if (!isSameDay(formValues.birthDate, originalValues.birthDate)) {
    _.set(diff, 'birthDate', formValues.birthDate)
  }

  if (originalValues.ethnicity?.id !== formValues.ethnicity?.value) {
    _.set(diff, 'ethnicity', formValues.ethnicity)
  }

  if (
    !_.isEqual(
      _.sortBy(_.keys(_.pickBy(formValues.information?.applyPermissions, Boolean))),
      _.sortBy(originalValues.information.applyPermissions),
    )
  ) {
    _.set(diff, 'information.applyPermissions', formValues.information.applyPermissions)
  }

  if (
    !_.isEqual(
      _.sortBy(_.keys(_.pickBy(formValues.information?.travelPermissions, Boolean))),
      _.sortBy(originalValues.information.travelPermissions),
    )
  ) {
    _.set(diff, 'information.travelPermissions', formValues.information.travelPermissions)
  }

  if (
    _.map(formValues.profile?.familySpokenLanguage, 'value').join(',')
    !== originalValues.profile.familySpokenLanguage
  ) {
    _.set(diff, 'profile.familySpokenLanguage', formValues.profile.familySpokenLanguage)
  }

  if (
    formValues.information.daytimeSleepingBehavior
    && `${i18n.t('global:Yes')} - ${formValues.information.daytimeSleepingBehaviorDetails}`
    !== originalValues.information.daytimeSleepingBehavior
  ) {
    _.set(diff, 'information.daytimeSleepingBehavior', true)
    _.set(diff, 'information.daytimeSleepingBehaviorDetails', formValues.information.daytimeSleepingBehaviorDetails)
  } else if (!formValues.information.daytimeSleepingBehavior && originalValues.information.daytimeSleepingBehavior) {
    _.set(diff, 'information.daytimeSleepingBehavior', false)
  }

  if (
    formValues.profile.englishAsAdditionalLanguage
    && `${i18n.t('global:Yes')} - ${formValues.profile.englishAsAdditionalLanguageDetails}`
    !== originalValues.profile.englishAsAdditionalLanguage
  ) {
    _.set(diff, 'profile.englishAsAdditionalLanguage', true)
    _.set(diff, 'profile.englishAsAdditionalLanguageDetails', formValues.profile.englishAsAdditionalLanguageDetails)
  } else if (!formValues.profile.englishAsAdditionalLanguage && originalValues.profile.englishAsAdditionalLanguage) {
    _.set(diff, 'profile.englishAsAdditionalLanguage', false)
  }

  if (
    formValues.profile.specialEducationalNeeds
    && `${i18n.t('global:Yes')} - ${formValues.profile.specialEducationalNeedsDetails}`
    !== originalValues.profile.specialEducationalNeeds
  ) {
    _.set(diff, 'profile.specialEducationalNeeds', true)
    _.set(diff, 'profile.specialEducationalNeedsDetails', formValues.profile.specialEducationalNeedsDetails)
  } else if (!formValues.profile.specialEducationalNeeds && originalValues.profile.specialEducationalNeeds) {
    _.set(diff, 'profile.specialEducationalNeeds', false)
  }

  return diff || {}
}

export const getBody = async ({
  child,
  nurseryFormBuilderSettings,
  onUploadImage,
  status,
  values,
}: {
  child: Child
  nurseryFormBuilderSettings: NurserySettingsFormBuilder
  onUploadImage: (selectedImage: SelectedImage) => Promise<string>
  status: RegistrationFormStatus
  values: FormPreviewFormValues
}): Promise<RegistrationFormBody> => {
  const { data } = values

  const body: RegistrationFormBody = {
    data: {
      information: {},
      profile: {},
    },
    status,
  }

  const formValues = getOnlyChangedValues(data, child)

  _.each([
    'firstName',
    'middleNames',
    'surname',
    'displayName',
    'codeReference',
    'taxFreeChildcareCodeReference',
    'fundingCode',
    'profile.siblings',
    'profile.parentsAreServingArmedForcesPersonnel',
    'profile.medicalInformation',
    'profile.isFussyEater',
    'profile.languageCapability',
    'profile.movementCapability',
    'profile.childBehavior',
    'profile.routine',
    'profile.attendsOtherSetting',
    'profile.toysPreference',
    'profile.booksPreference',
    'profile.foodsPreference',
    'profile.otherPreference',
    'information.address',
    'information.postcode',
    'information.canReceiveMedicine',
    'information.hasPermissionToBeTakenToHospital',
    'information.canConsumeFoodWithNutTraces',
    'information.toiletTrained',
    'information.drinkingMethod',
    'information.canBePhotographed',
    'information.canBeTaggedInGroupPhotos',
    'information.socialMediaPhotoSharingPermitted',
    'information.closedSocialMediaPhotoSharingPermitted',
    'information.useOnSiteMediaPermitted',
    'information.otherMarketingMaterialPhotoSharingPermitted',
  ], (key: string) => {
    const value = _.get(formValues, key)

    if (value !== undefined) {
      _.set(body.data, key, value)
    }
  })

  _.each([
    'gender',
    'religion',
    'profile.familyPosition',
    'profile.familyMaritalStatus',
    'information.allergies',
    'information.dietaryReq',
  ], (key: string) => {
    const valueTypeOption = _.get(formValues, `${key}.value`)

    if (valueTypeOption !== undefined) {
      _.set(body.data, key, valueTypeOption)
    }

    if (valueTypeOption === undefined) {
      const valueNoTypeOption = _.get(formValues, key)

      if (valueNoTypeOption !== undefined) {
        _.set(body.data, key, valueNoTypeOption)
      }
    }
  })

  if (formValues.photo?.file) {
    body.data.photo = await onUploadImage(formValues.photo)
  } else if (_.isString(formValues.photo)) {
    body.data.photo = formValues.photo
  } else if (null === formValues.photo) {
    body.data.photo = null
  }

  if (formValues.birthDate) {
    body.data.birthDate = moment(formValues.birthDate).format(DEFAULT_DATE_FORMAT)
  }

  if (formValues.ethnicity) {
    body.data.ethnicity = {
      id: formValues.ethnicity?.value,
    }
  }

  if (
    formValues?.information?.allergiesDetails
    && (
      formValues.information.allergies?.value === AllergyTypes.HAS_ALLERGY
      || formValues?.information?.allergies === AllergyTypes.HAS_ALLERGY
      || child?.information?.allergies === AllergyTypes.HAS_ALLERGY
    )
  ) {
    body.data.information.allergiesDetails = formValues.information.allergiesDetails
  }

  if (
    formValues?.information?.dietaryReqDesc
    && (
      formValues.information.dietaryReq?.value
        ? formValues.information.dietaryReq?.value !== DIETARY_NO_PREFERENCE
        : formValues?.information?.dietaryReq !== DIETARY_NO_PREFERENCE
    )
  ) {
    body.data.information.dietaryReqDesc = formValues.information.dietaryReqDesc
  }

  if (nurseryFormBuilderSettings.fields.child?.information?.daytimeSleepingBehavior) {
    if (formValues?.information?.daytimeSleepingBehavior) {
      body.data.information.daytimeSleepingBehavior = `${i18n.t('global:Yes')}`

      if (formValues.information?.daytimeSleepingBehaviorDetails) {
        body.data.information.daytimeSleepingBehavior += ` - ${formValues.information.daytimeSleepingBehaviorDetails}`
      }
    } else if (false === formValues?.information?.daytimeSleepingBehavior) {
      body.data.information.daytimeSleepingBehavior = undefined
    }
  }

  if (formValues?.profile?.englishAsAdditionalLanguage) {
    body.data.profile.englishAsAdditionalLanguage = `${i18n.t('global:Yes')}`

    if (formValues.profile?.englishAsAdditionalLanguageDetails) {
      body.data.profile.englishAsAdditionalLanguage += ` - ${data.profile.englishAsAdditionalLanguageDetails}`
    }
  } else if (false === formValues?.profile?.englishAsAdditionalLanguage) {
    body.data.profile.englishAsAdditionalLanguage = null
  }

  if (formValues?.profile?.specialEducationalNeeds) {
    body.data.profile.specialEducationalNeeds = `${i18n.t('global:Yes')}`

    if (formValues.profile?.specialEducationalNeedsDetails) {
      body.data.profile.specialEducationalNeeds += ` - ${formValues.profile.specialEducationalNeedsDetails}`
    }
  } else if (false === formValues?.profile?.specialEducationalNeeds) {
    body.data.profile.specialEducationalNeeds = null
  }

  if (formValues?.profile?.familySpokenLanguage) {
    body.data.profile.familySpokenLanguage = _.map(formValues.profile.familySpokenLanguage, 'value').join(',')
  }

  if (formValues?.information?.applyPermissions) {
    body.data.information.applyPermissions = _.keys(_.pickBy(formValues.information.applyPermissions, Boolean))
  }

  if (formValues?.information?.travelPermissions) {
    body.data.information.travelPermissions = _.keys(_.pickBy(formValues.information.travelPermissions, Boolean))
  }

  if (formValues?.carerChildRelations.length) {
    body.data.carerChildRelations = []
  }

  await Promise.all(_.map(formValues.carerChildRelations, async (carerChildRelation: FormPreviewCarerChildRelation) => {
    const carerItem: RegistrationFormBodyCarerChildRelation & { carer: { objectId?: number } } = {
      carer: {},
    }

    _.each([
      'address',
      'postCode',
      'email',
      'firstName',
      'surname',
      'mobile',
      'occupation',
      'telephone',
      'workTelephone',
    ], (key: string) => {
      const value = _.get(carerChildRelation.carer, key)

      if (value !== undefined) {
        _.set(carerItem.carer, key, value)
      }
    })

    if (carerChildRelation.objectId) {
      carerItem.objectId = carerChildRelation.objectId
    }

    if (carerChildRelation.carer?.objectId) {
      carerItem.carer.objectId = carerChildRelation.carer.objectId
    }

    if (!_.isUndefined(carerChildRelation.relation)) {
      carerItem.relation = carerChildRelation.relation?.label as string || carerChildRelation.relation as null
    }

    if (!_.isUndefined(carerChildRelation.carer?.title?.value)) {
      carerItem.carer.title = carerChildRelation.carer.title.value
    }

    if ((carerChildRelation.carer?.photo as SelectedImage)?.file) {
      carerItem.carer.photo = await onUploadImage(carerChildRelation.carer.photo as SelectedImage)
    } else if (_.isString(carerChildRelation.carer?.photo)) {
      carerItem.carer.photo = carerChildRelation.carer.photo as string
    } else if (null === carerChildRelation.carer?.photo) {
      carerItem.carer.photo = null
    }

    if (!_.keys(carerItem.carer).length) {
      delete carerItem.carer
    }

    if (RegistrationFormObjectAction.DELETE === carerChildRelation.objectAction) {
      delete carerItem.carer

      carerItem.objectAction = RegistrationFormObjectAction.DELETE

      if (!carerItem.objectId) {
        return
      }
    }

    if (!(
      1 === _.keys(carerItem?.carer).length && carerItem?.carer?.objectId
      && 2 === _.keys(carerItem).length && carerItem.objectId
    )) {
      body.data.carerChildRelations.push(carerItem)
    }
  }))

  if (!body.data.carerChildRelations.length) {
    delete body.data.carerChildRelations
  }

  if (values?.signature) {
    body.signature = {
      mimeType: values.signature?.file?.type,
      size: values.signature?.file?.size,
      url: await onUploadImage(values.signature),
    }
  }

  if (0 === _.keys(body.data.profile).length) {
    delete body.data.profile
  }

  if (0 === _.keys(body.data.information).length) {
    delete body.data.information
  }

  if (0 === _.keys(body.data).length) {
    delete body.data
  }

  return body
}
